import React, { useState, useMemo, useEffect } from 'react';
import { useFileUpload } from '../hooks/useFileUpload';
import { useDataFilters } from '../hooks/useDataFilters';
import { 
  calculateLevantadasMaoMetrics,
  analyzeAtendentesPerformance,
  analyzeLevantadasPorHorario,
  analyzeDistribuicaoTempoResposta,
  analyzeJanelasDeTempoLevantadas
} from '../utils/dataAnalysis';

// Componentes
import FileUpload from './FileUpload';
import LoadingSpinner from './LoadingSpinner';
import FilterSection from './FilterSection';
import LevantadasAnalysis from './LevantadasAnalysis';
import AtendentesTable from './AtendentesTable';
import DetailModal from './DetailModal';
import { 
  AtendentesPerformanceChart, 
  LevantadasPorHorarioChart, 
  DistribuicaoTempoRespostaChart
} from './Charts';

/**
 * Dashboard principal focado em Levantadas de Mão
 */
const Dashboard = () => {
  const [levantadasMao, setLevantadasMao] = useState([]);
  const [hasData, setHasData] = useState(false);
  const [detailView, setDetailView] = useState(null);

  const { loading, uploadError, handleFileUpload, setUploadError } = useFileUpload();
  const { filters, setFilters, filteredData, attendantsList } = useDataFilters(levantadasMao);

  // Processa período de análise
  const periodoAnalise = useMemo(() => {
    if (levantadasMao.length === 0) return { inicio: null, fim: null };
    
    const datas = levantadasMao
      .filter(item => item.levantadaMaoInicio)
      .map(item => item.levantadaMaoInicio)
      .sort((a, b) => a - b);
    
    return {
      inicio: datas[0],
      fim: datas[datas.length - 1]
    };
  }, [levantadasMao]);

  // Calcula métricas das levantadas de mão
  const metrics = useMemo(() => {
    return calculateLevantadasMaoMetrics(filteredData);
  }, [filteredData]);

  // Analisa performance dos atendentes
  const atendentesData = useMemo(() => {
    return analyzeAtendentesPerformance(filteredData);
  }, [filteredData]);

  // Analisa levantadas por horário
  const dadosHorarios = useMemo(() => {
    return analyzeLevantadasPorHorario(filteredData);
  }, [filteredData]);

  // Analisa distribuição de tempo de resposta
  const distribuicaoTempo = useMemo(() => {
    return analyzeDistribuicaoTempoResposta(filteredData);
  }, [filteredData]);

  // Analisa janelas de tempo
  // Removido: janelas de tempo

  // Handler para upload de arquivo
  const onFileUploadSuccess = (processedData) => {
    setLevantadasMao(processedData);
    setHasData(true);
    setUploadError(null);
  };

  // Função para mostrar detalhes
  const showDetail = (type, filter = {}) => {
    let detailData = [...filteredData];
    
    if (type === 'levantadas_com_mensagem') {
      detailData = detailData.filter(item => item.temMensagemLead);
    } else if (type === 'levantadas_respondidas') {
      detailData = detailData.filter(item => item.foiRespondida);
    } else if (type === 'levantadas_nao_respondidas') {
      detailData = detailData.filter(item => item.naoFoiRespondida);
    } else if (type === 'leads_multiplas') {
      const counts = new Map();
      for (const item of detailData) {
        const tel = item.telefone || 'NA';
        counts.set(tel, (counts.get(tel) || 0) + 1);
      }
      const telesMultiplas = new Set(
        Array.from(counts.entries())
          .filter(([_, c]) => c > 1)
          .map(([t]) => t)
      );
      detailData = detailData.filter(item => telesMultiplas.has(item.telefone || 'NA'));
    } else if (type === 'atendente_respondidas' && filter.atendente) {
      detailData = detailData.filter(item => {
        const v = item.atendenteResponsavel || '';
        const label = v.includes('@') ? v.split('@')[0] : v;
        return label === filter.atendente && item.foiRespondida;
      });
    } else if (type === 'atendente_nao_respondidas' && filter.atendente) {
      detailData = detailData.filter(item => {
        const v = item.atendenteResponsavel || '';
        const label = v.includes('@') ? v.split('@')[0] : v;
        return label === filter.atendente && item.naoFoiRespondida;
      });
    } else if (type === 'tempo_resposta_custom') {
      const min = typeof filter.min === 'number' ? filter.min : 0;
      const max = typeof filter.max === 'number' ? filter.max : Number.POSITIVE_INFINITY;
      detailData = detailData.filter(item => item.foiRespondida && typeof item.minutosResposta === 'number' && item.minutosResposta >= min && item.minutosResposta <= max);
    }
    
    setDetailView({ type, data: detailData, filter });
  };

  // Ouve ações "Ver registros" do Resumo por Categoria
  useEffect(() => {
    const handler = (e) => {
      const { type, faixa } = e.detail || {};
      if (type !== 'tempo_resposta_faixa') return;
      let detailData = filteredData.filter(item => item.foiRespondida && item.minutosResposta && item.minutosResposta > 0);
      if (faixa === 'ate60') {
        detailData = detailData.filter(item => item.minutosResposta < 60);
      } else if (faixa === '61a120') {
        detailData = detailData.filter(item => item.minutosResposta >= 61 && item.minutosResposta <= 120);
      } else if (faixa === '121mais') {
        detailData = detailData.filter(item => item.minutosResposta >= 121);
      }
      setDetailView({ type: 'distribuicao_tempo', data: detailData, filter: { faixa } });
    };
    window.addEventListener('open-details', handler);
    return () => window.removeEventListener('open-details', handler);
  }, [filteredData]);

  // Tela de loading
  if (loading) {
    return <LoadingSpinner />;
  }

  // Tela inicial sem dados
  if (!hasData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-3xl font-bold mb-2">Dashboard SeleneBOT</h1>
            <p className="text-orange-100">Análise de Levantadas de Mão - WhatsApp</p>
          </div>
        </div>

        <FileUpload 
          onFileUpload={(file) => handleFileUpload(file, onFileUploadSuccess)}
          uploadError={uploadError}
          loading={loading}
          hasData={hasData}
        />
      </div>
    );
  }

  // Dashboard principal com dados
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Dashboard SeleneBOT</h1>
            <p className="text-orange-100">
              Análise de Levantadas de Mão - {levantadasMao.length.toLocaleString()} janelas de conversão
              {periodoAnalise.inicio && periodoAnalise.fim && (
                <span className="block text-sm">
                  Período: {periodoAnalise.inicio.toLocaleDateString('pt-BR')} a {periodoAnalise.fim.toLocaleDateString('pt-BR')}
                </span>
              )}
            </p>
          </div>
          
          <FileUpload 
            onFileUpload={(file) => handleFileUpload(file, onFileUploadSuccess)}
            uploadError={uploadError}
            loading={loading}
            hasData={hasData}
          />
        </div>
      </div>

      {/* Conteúdo principal */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Erro de upload */}
        {uploadError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{uploadError}</p>
          </div>
        )}
        
        {/* Filtros */}
        <FilterSection filters={filters} setFilters={setFilters} attendantsList={attendantsList} />

        {/* Análise Principal das Levantadas */}
        <LevantadasAnalysis metrics={metrics} showDetail={showDetail} data={filteredData} />

        {/* Gráfico de Volume por Horário */}
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-6 mb-6">
          <LevantadasPorHorarioChart dadosHorarios={dadosHorarios} />
        </div>

        {/* Performance dos Atendentes */}
        <div className="mb-6">
          <AtendentesPerformanceChart atendentesData={atendentesData} />
        </div>

        {/* Distribuição de Tempo de Resposta */}
        <div className="mb-6">
          <DistribuicaoTempoRespostaChart distribuicaoTempo={distribuicaoTempo} />
        </div>

        {/* Removido: Janelas de Tempo */}

        {/* Tabela de Atendentes */}
        <AtendentesTable atendentesData={atendentesData} showDetail={showDetail} />

        {/* Resumo Final (sem bloco de Insights) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">📊 Resumo Executivo</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{metrics.totalLevantadasMao.toLocaleString()}</div>
              <div className="text-sm text-gray-600">Levantadas de Mão</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{metrics.taxaEngajamento.toFixed(2)}%</div>
              <div className="text-sm text-gray-600">Taxa de Engajamento do Lead</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{metrics.taxaResposta.toFixed(2)}%</div>
              <div className="text-sm text-gray-600">Taxa de Resposta do Atendente</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{atendentesData.length}</div>
              <div className="text-sm text-gray-600">Atendentes Ativos</div>
            </div>
          </div>
        </div>

        {/* Modal de detalhes */}
        <DetailModal detailView={detailView} setDetailView={setDetailView} />
      </div>
    </div>
  );
};

export default Dashboard;